import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

type GitHubUser = {
  name?: string;
  bio?: string;
  blog?: string;
  followers?: number;
  avatar_url?: string;
  html_url?: string;
};

type GitHubRepo = {
  id: number;
  name: string;
  description: string | null;
  html_url: string;
  homepage: string | null;
  fork: boolean;
  language: string | null;
  stargazers_count: number;
  updated_at: string;
};

const GH_USER = "flo8998";
const DEFAULT_WEBSITE = "https://mastra-gaming.de";

function Home() {
  const [user, setUser] = useState<GitHubUser | null>(null);
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingRepos, setLoadingRepos] = useState(true);
  const [userError, setUserError] = useState(false);
  const [repoError, setRepoError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>("—");

  useEffect(() => {
    document.title = "Florian – @flo8998 | Portfolio & Projekte";

    const metaDescription = document.querySelector(
      'meta[name="description"]',
    ) as HTMLMetaElement | null;

    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Portfolio, Projekte und Kontakt von Florian (@flo8998).",
      );
    }

    const fetchUser = async () => {
      try {
        setLoadingUser(true);
        setUserError(false);

        const response = await fetch(`https://api.github.com/users/${GH_USER}`);

        if (!response.ok) {
          throw new Error("GitHub user request failed");
        }

        const data: GitHubUser = await response.json();
        setUser(data);
        setLastUpdated(new Date().toLocaleString("de-DE"));
      } catch (error) {
        console.error("Fehler beim Laden des GitHub-Profils:", error);
        setUserError(true);
      } finally {
        setLoadingUser(false);
      }
    };

    const fetchRepos = async () => {
      try {
        setLoadingRepos(true);
        setRepoError(false);

        const response = await fetch(
          `https://api.github.com/users/${GH_USER}/repos?sort=updated&per_page=6`,
        );

        if (!response.ok) {
          throw new Error("GitHub repo request failed");
        }

        const data: GitHubRepo[] = await response.json();

        const sortedRepos = [...data]
          .sort(
            (a, b) =>
              new Date(b.updated_at).getTime() -
              new Date(a.updated_at).getTime(),
          )
          .slice(0, 6);

        setRepos(sortedRepos);
      } catch (error) {
        console.error("Fehler beim Laden der Repositories:", error);
        setRepoError(true);
      } finally {
        setLoadingRepos(false);
      }
    };

    fetchUser();
    fetchRepos();
  }, []);

  const profileName = useMemo(() => {
    if (user?.name?.trim()) return user.name;
    return "Florian";
  }, [user]);

  const profileBio = useMemo(() => {
    if (userError) return "Profil konnte nicht geladen werden.";
    if (loadingUser) return "Lade Profilinformationen …";
    return user?.bio?.trim() || "Builder, Tüftler und Open-Source-Fan.";
  }, [loadingUser, user, userError]);

  const profileAvatar = useMemo(() => {
    return user?.avatar_url || `https://github.com/${GH_USER}.png`;
  }, [user]);

  const websiteUrl = useMemo(() => {
    const blog = user?.blog?.trim();

    if (!blog) return DEFAULT_WEBSITE;

    if (blog.startsWith("http://") || blog.startsWith("https://")) {
      return blog;
    }

    return `https://${blog}`;
  }, [user]);

  const websiteLabel = useMemo(() => {
    try {
      return new URL(websiteUrl).host;
    } catch {
      return "mastra-gaming.de";
    }
  }, [websiteUrl]);

  const renderRepoCard = (repo: GitHubRepo) => {
    const updatedDate = new Date(repo.updated_at).toLocaleDateString("de-DE");

    return (
      <article key={repo.id} className="home-card repo-card">
        <h3>{repo.name}</h3>

        <p>{repo.description?.trim() || "Keine Beschreibung hinterlegt."}</p>

        <div className="repo-meta">
          <span className="chip chip-outline" title="Sprache">
            {repo.language || "—"}
          </span>

          <span className="chip chip-outline" title="Stars">
            ★ {repo.stargazers_count || 0}
          </span>

          <span className="chip chip-outline" title="Zuletzt aktualisiert">
            {updatedDate}
          </span>

          {repo.fork && (
            <span className="chip chip-warn" title="Fork">
              Fork
            </span>
          )}
        </div>

        <div className="repo-links">
          <a
            href={repo.html_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
          >
            Repo
          </a>

          {repo.homepage?.trim() && (
            <a
              href={repo.homepage}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              Demo
            </a>
          )}
        </div>
      </article>
    );
  };

  return (
    <main className="home-page">
      <section className="hero-section portfolio-home">
        <div className="hero-grid">
          <div className="hero-copy">
            <p className="kicker">Hallo, ich bin Florian</p>

            <h1 className="hero-title">
              Builder, Tüftler und <span>Open-Source-Fan ⚡</span>
            </h1>

            <p className="hero-description large">
              Auf dieser Seite findest du meine aktuellsten Repositories, ein
              bisschen Kontext und die Links, wie du mich erreichst. Die Liste
              unten lädt live aus der GitHub-API.
            </p>

            <div className="hero-actions">
              <a
                href={`https://github.com/${GH_USER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Auf GitHub folgen
              </a>

              <Link to="/projects" className="btn btn-secondary">
                Projekte ansehen
              </Link>

              <Link to="/terminal" className="btn btn-secondary">
                Terminal öffnen
              </Link>
            </div>
          </div>

          <div className="hero-profile-card">
            <div className="home-card">
              <h3>Kurzprofil</h3>

              <div className="about-box">
                <img
                  src={profileAvatar}
                  alt={`Avatar von ${profileName} (@${GH_USER})`}
                  className="profile-avatar"
                />

                <div className="about-meta">
                  <div className="profile-name">{profileName}</div>

                  <div className="badge">@{GH_USER}</div>

                  <div className="profile-bio">{profileBio}</div>

                  <div className="profile-stats">
                    <span className="chip chip-outline">
                      Follower:{" "}
                      {loadingUser
                        ? "…"
                        : typeof user?.followers === "number"
                          ? user.followers
                          : "—"}
                    </span>

                    <a
                      className="chip"
                      href={websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {websiteLabel}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="content-section">
        <div className="section-header">
          <h2>Über mich</h2>
          <p className="section-lead">
            Ich interessiere mich für Entwicklung, Tech, Linux, Gaming und
            alles, was kreativ mit Software gebaut werden kann.
          </p>
        </div>

        <div className="info-grid extended">
          <div className="glass-card">
            <h3>Tech Stack</h3>
            <p>
              React, TypeScript, JavaScript, CSS, Routing, UI Engineering und
              moderne Webentwicklung.
            </p>
          </div>

          <div className="glass-card">
            <h3>Style</h3>
            <p>
              Dark Theme, klare Struktur, moderne Cards, Terminal-Vibes und
              lebendige Portfolio-Elemente.
            </p>
          </div>

          <div className="glass-card">
            <h3>Ziel</h3>
            <p>
              Eine persönliche Website mit Charakter, echten Projekten und
              Inhalten, die direkt mit GitHub verbunden sind.
            </p>
          </div>
        </div>
      </section>

      <section className="content-section" id="projects">
        <div className="section-header">
          <h2>Neueste Repositories</h2>
          <p className="section-lead">
            Direkt aus der GitHub-API geladen, nach Aktualität sortiert. Forks
            werden markiert.
          </p>
        </div>

        <div className="repo-grid">
          {loadingRepos &&
            Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="home-card repo-skeleton">
                <div className="skeleton skeleton-title" />
                <div className="skeleton skeleton-text" />
                <div className="skeleton skeleton-text short" />
              </div>
            ))}

          {!loadingRepos &&
            !repoError &&
            repos.length > 0 &&
            repos.map(renderRepoCard)}

          {!loadingRepos && !repoError && repos.length === 0 && (
            <div className="home-card">
              <p>Keine Repositories gefunden.</p>
            </div>
          )}

          {!loadingRepos && repoError && (
            <div className="home-card">
              <p>Fehler beim Laden der Repositories.</p>
            </div>
          )}
        </div>

        <div className="section-actions">
          <a
            href={`https://github.com/${GH_USER}?tab=repositories`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
          >
            Alle Repositories ansehen →
          </a>
        </div>
      </section>

      <section className="content-section" id="contact">
        <div className="section-header">
          <h2>Kontakt & Links</h2>
        </div>

        <div className="info-grid extended">
          <div className="glass-card">
            <h3>GitHub</h3>
            <p>
              Issues, Feedback oder einfach mal durch meine Projekte stöbern.
            </p>
            <div className="repo-links">
              <a
                href={`https://github.com/${GH_USER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                @{GH_USER} öffnen
              </a>
            </div>
          </div>

          <div className="glass-card">
            <h3>Website</h3>
            <p>Hier kommt man direkt zu meiner Website.</p>
            <div className="repo-links">
              <a
                href={DEFAULT_WEBSITE}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                mastra-gaming.de besuchen
              </a>
            </div>
          </div>

          <div className="glass-card">
            <h3>Diese Seite</h3>
            <p>
              Das hier ist mein Portfolio-Bereich mit GitHub-Anbindung und
              aktuellen Projekten.
            </p>
            <div className="repo-links">
              <a
                href={`https://github.com/${GH_USER}/${GH_USER}.github.io`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                Repository öffnen
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="home-footer">
        <small>
          © {new Date().getFullYear()} Florian (@
          <a
            href={`https://github.com/${GH_USER}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {GH_USER}
          </a>
          ). Letztes Update: {lastUpdated}.
        </small>
      </footer>
    </main>
  );
}

export default Home;
